/**
 * AadhaarEnrollForm.tsx
 * Create ABHA by Aadhaar — multi-step form matching HealthProPluse flow
 *
 * Flow:
 *   Step 1 — Enter 12-digit Aadhaar + consent → Send OTP
 *   Step 2 — Enter OTP + communication mobile → Verify OTP
 *   Step 2b — Verify new mobile OTP (if different from Aadhaar-linked)
 *   → ABHACreator (select ABHA address)
 */

import React, { useState, useEffect } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { handleRequestOTP, handleVerifyOTP, getAbhaSuggestions } from '../../utils/api';

type InnerStep = 'aadhaar_input' | 'otp_verify' | 'mobile_otp';

const CONSENT_ITEMS = [
    { id: 'aadhaar', label: 'I hereby declare that I am voluntarily sharing my Aadhaar number / Virtual ID issued by UIDAI, and I am aware that my Aadhaar data will be processed for the purpose of creating my ABHA number.', disabled: false },
    { id: 'noAadhaar', label: 'I understand that ABHA can also be created without Aadhaar.', disabled: true },
    { id: 'legacy', label: 'I consent to the usage of my Aadhaar details for the purpose of creating ABHA number as per the provisions of the Aadhaar Act 2016 and subsequent amendments.', disabled: false },
    { id: 'sharing', label: 'I authorize NHA to share my ABHA number and ABHA address with the healthcare providers for the purpose of connecting to my health records.', disabled: false },
    { id: 'anonymization', label: 'I consent to the anonymization of my health records for use in research and public health purposes.', disabled: false },
    { id: 'healthWorker', label: 'I have been informed and understand that the healthcare worker assisting me in creating my ABHA has my consent to proceed.', disabled: false },
] as const;

type ConsentKey = typeof CONSENT_ITEMS[number]['id'];

export const AadhaarEnrollForm: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { authData, authConfig, transactionId, xToken } = state;

    const [step, setStep] = useState<InnerStep>('aadhaar_input');

    // Step 1
    const [aadhaarNumber, setAadhaarNumber] = useState('');
    const [showAadhaar, setShowAadhaar] = useState(false);
    const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({
        aadhaar: false, noAadhaar: false, legacy: false,
        sharing: false, anonymization: false, healthWorker: false,
    });
    const [beneficiaryName, setBeneficiaryName] = useState('');
    const [beneficiaryConsent, setBeneficiaryConsent] = useState(false);

    // Step 2
    const [otp, setOtp] = useState('');
    const [otpTimer, setOtpTimer] = useState(60);
    const [resendCount, setResendCount] = useState(0);
    const [maskedMobile, setMaskedMobile] = useState('');
    const [aadhaarPhone, setAadhaarPhone] = useState('');

    // Step 2b
    const [newPhone, setNewPhone] = useState('');
    const [mobileOtp, setMobileOtp] = useState('');
    const [mobileOtpTimer, setMobileOtpTimer] = useState(60);
    const [mobileTxnId, setMobileTxnId] = useState('');

    // Shared
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // ABHA Account Exists popup
    const [showExistsModal, setShowExistsModal] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [existingProfileForModal, setExistingProfileForModal] = useState<any>(null);
    const [showProfileDetails, setShowProfileDetails] = useState(false);

    // Timers
    useEffect(() => {
        if (step !== 'otp_verify' || otpTimer <= 0) return;
        const id = setInterval(() => setOtpTimer(s => s - 1), 1000);
        return () => clearInterval(id);
    }, [step, otpTimer]);

    useEffect(() => {
        if (step !== 'mobile_otp' || mobileOtpTimer <= 0) return;
        const id = setInterval(() => setMobileOtpTimer(s => s - 1), 1000);
        return () => clearInterval(id);
    }, [step, mobileOtpTimer]);

    // Derived
    const disabledConsentIds = CONSENT_ITEMS.filter(c => c.disabled).map(c => c.id);
    const allConsentsChecked = Object.entries(consents).every(([key, val]) => disabledConsentIds.includes(key) || val);
    const allConsentsAgreed = allConsentsChecked && beneficiaryConsent && beneficiaryName.trim().length > 0;
    const aadhaarValid = aadhaarNumber.length === 12;
    const step1Ready = aadhaarValid && allConsentsAgreed;
    const aadhaarPhoneValid = aadhaarPhone.length === 10;
    const step2Ready = otp.length === 6 && aadhaarPhoneValid;

    const masked5 = (val: string) =>
        val.length >= 5 ? `*****${val.slice(-5)}` : '*****';

    // Toggle all consents
    const toggleAll = () => {
        const next = !allConsentsChecked;
        const updated: Record<string, boolean> = {};
        CONSENT_ITEMS.forEach(item => { updated[item.id] = item.disabled ? false : next; });
        setConsents(updated as Record<ConsentKey, boolean>);
        setBeneficiaryConsent(next);
    };

    // Mask ABHA number for display
    const maskAbhaNumber = (abha: string) => {
        if (!abha) return '';
        const parts = abha.split('-');
        if (parts.length === 4) return `xx-xxxx-xxxx-${parts[3]}`;
        return abha.length > 4 ? `${'x'.repeat(abha.length - 4)}${abha.slice(-4)}` : abha;
    };

    // ═══ HANDLERS ═══

    // Step 1 — Send OTP
    const handleSendAadhaarOtp = async () => {
        setLoading(true);
        setError('');
        try {
            const updatedConfig = {
                ...authConfig,
                type: 'enrollment' as const,
                method: 'aadhaar' as const,
                value: aadhaarNumber,
                aadhaarMobileInput: '',
                enrollmentMode: 'aadhaar' as const,
            };
            dispatch({ type: 'SET_AUTH_CONFIG', payload: updatedConfig });
            const result = await handleRequestOTP(authData!, updatedConfig);
            dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            if (result.maskedMobile) setMaskedMobile(result.maskedMobile);
            setStep('otp_verify');
            setOtpTimer(60);
        } catch (err) {
            const msg = (err as Error).message || '';
            if (msg.includes('Invalid LoginId') || msg.includes('400') || msg.includes('invalid') || msg.includes('Aadhaar')) {
                setError('Aadhaar Number is not valid');
            } else {
                setError(msg || 'Failed to send OTP. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Step 2 — Resend OTP
    const handleResendAadhaarOtp = async () => {
        if (resendCount >= 2) return;
        setLoading(true);
        setError('');
        setOtp('');
        try {
            const result = await handleRequestOTP(authData!, authConfig);
            dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            if (result.maskedMobile) setMaskedMobile(result.maskedMobile);
            setOtpTimer(60);
            setResendCount(c => c + 1);
        } catch (err) {
            setError((err as Error).message || 'Resend failed.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2 — Verify Aadhaar OTP
    const handleVerifyAadhaarOtp = async () => {
        setLoading(true);
        setError('');
        try {
            const verifyConfig = { ...authConfig, aadhaarMobileInput: aadhaarPhone };
            dispatch({ type: 'SET_AUTH_CONFIG', payload: verifyConfig });

            const result = await handleVerifyOTP(authData!, verifyConfig, transactionId, otp);
            if (result.xToken) dispatch({ type: 'SET_X_TOKEN', payload: result.xToken });
            if (result.txnId) dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            if (result.profile) dispatch({ type: 'SET_USER_PROFILE', payload: result.profile });

            // Pre-fetch ABHA suggestions
            try {
                const sugTxnId = result.txnId || transactionId;
                const sugXToken = result.xToken || xToken;
                if (sugTxnId && sugXToken) {
                    const sugList = await getAbhaSuggestions(authData!, sugTxnId, sugXToken);
                    if (sugList.length > 0) dispatch({ type: 'SET_ABHA_SUGGESTIONS', payload: sugList });
                }
            } catch { /* suggestions are non-critical */ }

            // Auto-decide mobile flow
            const isExistingUser = result.profile && result.isEnrollment;
            const profileMobile = result.profile?.mobile || '';
            const enteredLast4 = aadhaarPhone.slice(-4);
            const maskedLast4 = maskedMobile.slice(-4);

            if (isExistingUser && profileMobile) {
                if (aadhaarPhone === profileMobile) {
                    setExistingProfileForModal(result.profile);
                    setShowExistsModal(true);
                    setLoading(false);
                    return;
                } else {
                    await sendMobileOtpDirect(aadhaarPhone);
                }
            } else {
                if (enteredLast4 === maskedLast4) {
                    dispatch({ type: 'SET_STEP', payload: 'abhaCreate' });
                } else {
                    await sendMobileOtpDirect(aadhaarPhone);
                }
            }
        } catch (err) {
            const errMsg = (err as Error).message || '';
            if (errMsg.toLowerCase().includes('otp') || errMsg.toLowerCase().includes('expired') || errMsg.toLowerCase().includes('incorrect') || errMsg.toLowerCase().includes('invalid')) {
                setError('Invalid Aadhaar OTP. Please enter a valid OTP.');
            } else {
                setError(errMsg || 'Please enter a valid OTP.');
            }
            setOtp('');
        } finally {
            setLoading(false);
        }
    };

    // Send OTP to mobile
    const sendMobileOtpDirect = async (phone: string) => {
        setLoading(true);
        setError('');
        try {
            const mobileConfig = {
                ...authConfig,
                type: 'enrollment' as const,
                method: 'mobile' as const,
                value: phone,
                aadhaarMobileInput: phone,
                enrollmentMode: 'aadhaar' as const,
            };
            const result = await handleRequestOTP(authData!, mobileConfig, transactionId, xToken);
            setNewPhone(phone);
            setMobileTxnId(result.txnId);
            setMobileOtpTimer(60);
            setStep('mobile_otp');
        } catch (err) {
            setError((err as Error).message || 'Failed to send OTP.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2b — Resend mobile OTP
    const handleResendMobileOtp = async () => {
        setLoading(true);
        setError('');
        setMobileOtp('');
        try {
            const mobileConfig = {
                ...authConfig,
                type: 'enrollment' as const,
                method: 'mobile' as const,
                value: newPhone,
                aadhaarMobileInput: newPhone,
                enrollmentMode: 'aadhaar' as const,
            };
            const result = await handleRequestOTP(authData!, mobileConfig, mobileTxnId, xToken);
            setMobileTxnId(result.txnId);
            setMobileOtpTimer(60);
        } catch (err) {
            setError((err as Error).message || 'Resend failed.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2b — Verify mobile OTP
    const handleVerifyMobileOtp = async () => {
        setLoading(true);
        setError('');
        try {
            const mobileConfig = {
                ...authConfig,
                type: 'enrollment' as const,
                method: 'mobile' as const,
                value: newPhone,
                aadhaarMobileInput: newPhone,
                enrollmentMode: 'aadhaar' as const,
            };
            const result = await handleVerifyOTP(authData!, mobileConfig, mobileTxnId, mobileOtp, xToken);
            if (result.xToken) dispatch({ type: 'SET_X_TOKEN', payload: result.xToken });
            if (result.txnId) dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            dispatch({ type: 'SET_AUTH_CONFIG', payload: { ...authConfig, aadhaarMobileInput: newPhone } });
            if (state.userProfile) {
                dispatch({ type: 'SET_USER_PROFILE', payload: { ...state.userProfile, mobile: newPhone } });
            }
            dispatch({ type: 'SET_STEP', payload: 'abhaCreate' });
        } catch (err) {
            setError((err as Error).message || 'Please enter a valid OTP.');
            setMobileOtp('');
        } finally {
            setLoading(false);
        }
    };

    if (!authData) return null;

    return (
        <div>
            {/* ═══ ABHA Account Exists Modal ═══ */}
            {showExistsModal && existingProfileForModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' }}>
                        {/* Close → go to ABHACreator */}
                        <button onClick={() => {
                            setShowExistsModal(false);
                            setShowProfileDetails(false);
                            setExistingProfileForModal(null);
                            dispatch({ type: 'SET_STEP', payload: 'abhaCreate' });
                        }} style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1, width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                            ✕
                        </button>

                        {/* Header */}
                        <div style={{ background: 'linear-gradient(135deg, #fff7ed, #fef3c7)', padding: '1.5rem', textAlign: 'center', borderBottom: '1px solid #fed7aa' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem', fontSize: '1.5rem' }}>
                                ⚠️
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#92400e' }}>
                                ABHA Account Exists
                            </h3>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.88rem', color: '#a16207', lineHeight: 1.5 }}>
                                We found an ABHA <strong>{maskAbhaNumber(existingProfileForModal.abhaNumber || '')}</strong> against the Aadhaar number provided.
                            </p>
                        </div>

                        {/* Profile card */}
                        <div style={{ padding: '1rem 1.5rem' }}>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>
                                    {(existingProfileForModal.fullName || 'A').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#14532d' }}>
                                        {existingProfileForModal.fullName || 'Name on Aadhaar'}
                                    </p>
                                    {(existingProfileForModal.abhaAddress || existingProfileForModal.preferredAbhaAddress) && (
                                        <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#166534' }}>
                                            🛡️ {existingProfileForModal.abhaAddress || existingProfileForModal.preferredAbhaAddress}
                                        </p>
                                    )}
                                    <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#15803d' }}>KYC Verified</p>
                                </div>
                            </div>
                        </div>

                        {/* Expanded details */}
                        {showProfileDetails && (
                            <div style={{ padding: '0 1.5rem 0.75rem' }}>
                                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '0.85rem', fontSize: '0.85rem', color: '#334155' }}>
                                    {existingProfileForModal.abhaNumber && <p style={{ margin: '4px 0' }}><strong>ABHA Number:</strong> {existingProfileForModal.abhaNumber}</p>}
                                    {(existingProfileForModal.abhaAddress || existingProfileForModal.preferredAbhaAddress) && <p style={{ margin: '4px 0' }}><strong>ABHA Address:</strong> {existingProfileForModal.abhaAddress || existingProfileForModal.preferredAbhaAddress}</p>}
                                    {existingProfileForModal.mobile && <p style={{ margin: '4px 0' }}><strong>Mobile:</strong> {existingProfileForModal.mobile}</p>}
                                    {existingProfileForModal.gender && <p style={{ margin: '4px 0' }}><strong>Gender:</strong> {existingProfileForModal.gender}</p>}
                                    {existingProfileForModal.dob && <p style={{ margin: '4px 0' }}><strong>DOB:</strong> {existingProfileForModal.dob}</p>}
                                </div>
                            </div>
                        )}

                        {/* View Profile toggle */}
                        <div style={{ padding: '0 1.5rem 1.5rem' }}>
                            <button onClick={() => setShowProfileDetails(!showProfileDetails)} className="btn-primary" style={{ width: '100%', background: 'linear-gradient(135deg, #f59e0b, #d97706)', marginBottom: '0.5rem' }}>
                                👤 {showProfileDetails ? 'Hide Profile' : 'View Profile'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="step-container">
                {/* ═══ STEP 1: Aadhaar + Consent ═══ */}
                {step === 'aadhaar_input' && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>🪪</span>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Create ABHA by Aadhaar</h2>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                                    Enter your Aadhaar — OTP will be sent to your Aadhaar-linked mobile
                                </p>
                            </div>
                        </div>

                        {/* Step indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '1rem' }}>
                            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#0d9488', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>1</span>
                            <span style={{ width: 40, height: 2, background: '#e2e8f0' }} />
                            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e2e8f0', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>2</span>
                        </div>

                        {/* Aadhaar input */}
                        <div className="form-group">
                            <label htmlFor="aadhaarNo">🪪 Aadhaar Number <span style={{ color: '#ef4444' }}>*</span></label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    id="aadhaarNo"
                                    type={showAadhaar ? 'text' : 'password'}
                                    inputMode="numeric"
                                    maxLength={12}
                                    value={aadhaarNumber}
                                    onChange={e => setAadhaarNumber(e.target.value.replace(/\D/g, ''))}
                                    placeholder="Enter 12-digit Aadhaar number"
                                    autoComplete="off"
                                    style={{ flex: 1 }}
                                />
                                <button type="button" onClick={() => setShowAadhaar(!showAadhaar)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', marginLeft: '8px', padding: '4px' }}>
                                    {showAadhaar ? '🙈' : '👁️'}
                                </button>
                            </div>
                            {aadhaarNumber.length > 0 && !aadhaarValid && (
                                <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>Must be exactly 12 digits ({aadhaarNumber.length}/12)</small>
                            )}
                            {aadhaarValid && (
                                <small style={{ color: '#10b981', marginTop: '4px', display: 'block' }}>✓ Valid — OTP will be sent to Aadhaar-linked mobile</small>
                            )}
                        </div>

                        {/* Consent block */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Patient Consent</h4>
                                <button onClick={toggleAll} style={{ background: 'none', border: 'none', color: '#0d9488', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                                    {allConsentsChecked ? 'Uncheck All' : 'Check All'}
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {CONSENT_ITEMS.map(item => (
                                    <label key={item.id} style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: item.disabled ? 'default' : 'pointer', fontSize: '0.78rem', lineHeight: 1.4, color: item.disabled ? '#94a3b8' : '#475569' }}>
                                        <input
                                            type="checkbox"
                                            checked={consents[item.id]}
                                            disabled={item.disabled}
                                            onChange={e => setConsents(prev => ({ ...prev, [item.id]: e.target.checked }))}
                                            style={{ marginTop: 2 }}
                                        />
                                        <span>{item.label}</span>
                                    </label>
                                ))}

                                {/* Beneficiary name consent */}
                                <div style={{ marginTop: '6px', padding: '10px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer', fontSize: '0.78rem', color: '#0f172a', fontWeight: 600 }}>
                                        <input type="checkbox" checked={beneficiaryConsent} onChange={e => setBeneficiaryConsent(e.target.checked)} style={{ marginTop: 2 }} />
                                        <div style={{ flex: 1 }}>
                                            I,{' '}
                                            <input
                                                type="text"
                                                placeholder="Beneficiary Name (as on Aadhaar)"
                                                value={beneficiaryName}
                                                onChange={e => setBeneficiaryName(e.target.value)}
                                                style={{ border: 'none', borderBottom: '1px solid #cbd5e1', outline: 'none', padding: '0 4px', width: '200px', fontWeight: 800, color: '#0d9488', background: 'transparent' }}
                                            />
                                            {' '}hereby give my consent for creation of my ABHA.
                                        </div>
                                    </label>
                                    <p style={{ margin: '4px 0 0 26px', fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                        Please enter the beneficiary's name exactly as it appears on their Aadhaar card.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button className="btn-primary" disabled={!step1Ready || loading} onClick={handleSendAadhaarOtp} style={{ marginTop: '0.5rem' }}>
                            {loading ? '📡 Sending OTP...' : '🔐 Send OTP to Aadhaar-linked Mobile'}
                        </button>

                        <button className="btn-secondary" onClick={() => dispatch({ type: 'SET_STEP', payload: 'authType' })} style={{ marginTop: '0.5rem' }}>
                            ⬅ Back
                        </button>
                    </>
                )}

                {/* ═══ STEP 2: Verify Aadhaar OTP + Phone ═══ */}
                {step === 'otp_verify' && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>🔐</span>
                            <h2 style={{ margin: '0.5rem 0 0.25rem' }}>Verify OTP</h2>
                            <p style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: 380, margin: '0 auto' }}>
                                OTP sent to <strong style={{ color: '#0d9488', fontFamily: 'monospace' }}>{maskedMobile || '*******XXXX'}</strong> linked with Aadhaar. Enter the OTP below.
                            </p>
                        </div>

                        {/* Step indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '1rem' }}>
                            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>✓</span>
                            <span style={{ width: 40, height: 2, background: '#0d9488' }} />
                            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#0d9488', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>2</span>
                        </div>

                        {/* OTP input */}
                        <div className="form-group" style={{ textAlign: 'center' }}>
                            <label>Enter 6-digit OTP</label>
                            <input
                                type="text"
                                maxLength={6}
                                value={otp}
                                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                                style={{ textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.4rem', fontWeight: 800, maxWidth: '240px', margin: '0 auto' }}
                                placeholder="------"
                                autoFocus
                            />
                        </div>

                        {/* Resend timer */}
                        <div style={{ textAlign: 'center', margin: '0.25rem 0 1rem', fontSize: '0.82rem' }}>
                            {resendCount >= 2 ? (
                                <p style={{ color: '#ef4444', fontWeight: 600 }}>Maximum OTP resend limit reached.</p>
                            ) : otpTimer > 0 ? (
                                <p style={{ color: '#64748b' }}>Resend in <strong style={{ color: '#0d9488' }}>{otpTimer}s</strong> (Attempts: {2 - resendCount}/2)</p>
                            ) : (
                                <button onClick={handleResendAadhaarOtp} disabled={loading} style={{ background: 'transparent', border: 'none', color: '#0d9488', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                                    📡 Resend OTP ({2 - resendCount}/2 remaining)
                                </button>
                            )}
                        </div>

                        {/* Communication mobile */}
                        <div className="form-group">
                            <label htmlFor="aadhaarPhone">
                                📱 Mobile number <span style={{ color: '#ef4444' }}>*</span>
                                <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.72rem', marginLeft: 4 }}>for ABHA communication</span>
                            </label>
                            <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '0 0 0.5rem' }}>
                                Enter your Aadhaar-linked number to continue, or a different number to update ABHA communication mobile
                            </p>
                            <input
                                id="aadhaarPhone"
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={aadhaarPhone}
                                onChange={e => { setAadhaarPhone(e.target.value.replace(/\D/g, '')); setError(''); }}
                                placeholder="Enter 10-digit mobile number"
                            />
                            {aadhaarPhone.length > 0 && !aadhaarPhoneValid && (
                                <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>Must be exactly 10 digits ({aadhaarPhone.length}/10)</small>
                            )}
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="action-buttons">
                            <button className="btn-secondary" onClick={() => { setStep('aadhaar_input'); setOtp(''); setAadhaarPhone(''); setError(''); }}>
                                ⬅ Back
                            </button>
                            <button className="btn-primary" disabled={!step2Ready || loading} onClick={handleVerifyAadhaarOtp}>
                                {loading ? '🔍 Verifying...' : '✅ Confirm OTP'}
                            </button>
                        </div>

                        <div style={{ marginTop: '10px', textAlign: 'center' }}>
                            <button onClick={() => dispatch({ type: 'RESET_FLOW' })} className="btn-outline" style={{ fontSize: '0.85em' }}>
                                Cancel & Start Over
                            </button>
                        </div>
                    </>
                )}

                {/* ═══ STEP 2b: Verify New Mobile OTP ═══ */}
                {step === 'mobile_otp' && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>📱</span>
                            <h2 style={{ margin: '0.5rem 0 0.25rem' }}>Verify New Mobile</h2>
                            <p style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: 360, margin: '0 auto' }}>
                                OTP sent to <strong>{masked5(newPhone)}</strong>. Enter it below to link this number to your ABHA.
                            </p>
                        </div>

                        <div className="form-group" style={{ textAlign: 'center' }}>
                            <label>Enter 6-digit OTP</label>
                            <input
                                type="text"
                                maxLength={6}
                                value={mobileOtp}
                                onChange={e => { setMobileOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                                style={{ textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.4rem', fontWeight: 800, maxWidth: '240px', margin: '0 auto' }}
                                placeholder="------"
                                autoFocus
                            />
                        </div>

                        <div style={{ textAlign: 'center', margin: '0.5rem 0 1rem', fontSize: '0.82rem' }}>
                            {mobileOtpTimer > 0 ? (
                                <p style={{ color: '#64748b' }}>Resend in <strong style={{ color: '#0d9488' }}>{mobileOtpTimer}s</strong></p>
                            ) : (
                                <button onClick={handleResendMobileOtp} disabled={loading} style={{ background: 'transparent', border: 'none', color: '#0d9488', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                                    📡 Resend OTP
                                </button>
                            )}
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="action-buttons">
                            <button className="btn-secondary" onClick={() => { setStep('otp_verify'); setMobileOtp(''); setError(''); }}>
                                ⬅ Back
                            </button>
                            <button className="btn-primary" disabled={mobileOtp.length < 6 || loading} onClick={handleVerifyMobileOtp}>
                                {loading ? '🔍 Verifying...' : '✅ Verify & Continue'}
                            </button>
                        </div>

                        <div style={{ marginTop: '10px', textAlign: 'center' }}>
                            <button onClick={() => dispatch({ type: 'RESET_FLOW' })} className="btn-outline" style={{ fontSize: '0.85em' }}>
                                Cancel & Start Over
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
